import moment from 'moment-timezone';

import React, { useState } from 'react';
import { Column, CellProps } from 'react-table';
import { Switch } from '@blueprintjs/core';
import { faBell, faCircle } from '@fortawesome/pro-solid-svg-icons';

import {
  CellFunction,
  MethodCell,
  RESPONSE_NONE,
  RESPONSE_FUTURE
} from '../cells';
import Card from '../../card';
import Pagination from '../../pagination';
import { DebouncedDialog } from '../../dialog';
import { LoadingBlock } from '../../loading/progress';
import StatusIndicator from '../../status/status-indicator';
import Table from '../index';
import VerticalTable from '../vertical';

import { IAlert } from '../../../../libs/alert';
import {
  ISession,
  SessionTransformer,
  useGetSessions
} from '../../../../libs/session';
import { GetCurrentTZ } from '../../../../libs/time';
import { AppKey } from '../../../../libs/apps';

import { SessionTableProvider } from './context';
import { InvalidationForm, InvalidationCell } from './invalidation';
import { useTerms } from '../../../../contexts/terms';
import { ICustomProgram } from '../../../../libs/composer';

const CURRENT_TZ = GetCurrentTZ();

export interface SessionTableInstanceProps {
  params?: any;
  customProgram?: ICustomProgram;
}

const SESSIONS_LIMIT = 15;

/**
 * A wrapper to output the cell values properly based on session state
 *
 * @param cell the cell function to wrap around
 */
export const ResponseGuard = <T extends ISession>(
  cell: CellFunction<T>
): CellFunction<T> => {
  return (args) => {
    const session = args.row.original;
    // Future session
    if (new Date(session.time) > new Date()) {
      return RESPONSE_FUTURE;
    }
    // Session not responded to, but check if it has been invalidated
    if (!session.respondedAt && !session.invalidation) {
      return RESPONSE_NONE;
    }
    return React.createElement(cell as React.ComponentType<CellProps<T>>, args);
  };
};

const ComposeColumns = <T extends ISession>(
  props: SessionTableProps<T>,
  _columns: Column<T>[]
): Column<T>[] => {
  const {
    type,
    disableDefaultColumns,
    disableTypeColumn,
    appKey,
    invalidationModel
  } = props;
  let columns: Column<T>[] = [];
  if (type !== 'vertical' && appKey !== 'surveyqual') {
    columns.push({
      Header: <StatusIndicator title="Status" color="gray3" />,
      id: '_status',
      Cell: ({ row: { original: session } }) => {
        // Check for alerts array first (for custom programs with multiple alerts)
        const alerts: IAlert[] =
          (session as any).alerts || (session.alert ? [session.alert] : []);
        if (alerts.length > 0) {
          // Render alerts as separate divs for proper line breaks and width control
          const tooltipContent = (
            <div style={{ textAlign: 'left' }}>
              {alerts.map((alert, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: index < alerts.length - 1 ? '8px' : '0'
                  }}
                >
                  {alert.body}
                </div>
              ))}
            </div>
          );
          return (
            <StatusIndicator
              title="Alert"
              indicator={faBell}
              color="blue3"
              tooltip={tooltipContent}
            />
          );
        }
        return (
          <StatusIndicator title="Session" indicator={faCircle} color="black" />
        );
      }
    });
  }
  columns = columns.concat(_columns);
  if (!disableDefaultColumns) {
    columns = columns.concat([
      {
        Header: 'Responded At',
        accessor: 'respondedAt',
        Cell: ({ cell: { value }, row: { original: session } }) => {
          if (!value) {
            return RESPONSE_NONE;
          }
          const tz = session?.schedule?.tz || session?.tz || CURRENT_TZ;
          const time = moment.tz(value as Date, tz);
          return <span>{time.format('MM/DD hh:mm A')}</span>;
        }
      },
      {
        Header: 'Msg Time',
        accessor: 'time',
        Cell: ({ cell: { value }, row: { original: session } }) => {
          const tz = session?.schedule?.tz || session?.tz || CURRENT_TZ;
          const time = moment.tz(value as Date, tz);
          return <span>{time.format('MM/DD/YY hh:mm A')}</span>;
        }
      }
    ]);
    if (!disableTypeColumn) {
      columns.push({
        Header: 'Msg Type',
        id: 'callIds',
        Cell: MethodCell()
      });
    }
  }
  if (appKey != null && invalidationModel != null) {
    columns.push({
      Header: 'Action',
      id: 'action',
      Cell: InvalidationCell(appKey, invalidationModel)
    });
  }
  return columns;
};

type TableType = 'horizontal' | 'vertical';

export interface SessionTableProps<
  T extends ISession,
  R extends ISession = never
> {
  type?: TableType;
  striped?: boolean;
  bareCell?: boolean;
  title?: string;
  appKey?: AppKey;
  url: string;
  params?: any;
  defaultLimit?: number;
  columns?: Column<T>[];
  getColumns?: (sessions: T[]) => Column<T>[];
  getDependencies?: (sessions: T[]) => any[];
  transformer?: SessionTransformer<R, T>;
  disableDefaultColumns?: boolean;
  disableTypeColumn?: boolean;
  disableControls?: boolean;
  invalidationModel?: string;
  disableFuture?: boolean;
  setIntakeData?: any;
  filterOutIntake?: boolean;
}

const DEFAULT_IVD_PROPS = {
  appKey: null,
  sessionModel: null,
  session: null
};

const SessionTable = <T extends ISession, R extends ISession = never>(
  props: SessionTableProps<T, R>
): JSX.Element => {
  const {
    type,
    striped,
    bareCell,
    title,
    url,
    params,
    defaultLimit,
    getColumns,
    getDependencies,
    transformer,
    disableControls,
    disableFuture = false,
    setIntakeData,
    filterOutIntake
  } = props;
  const { terms } = useTerms();
  // Setup filtering states
  const [includeCanceled, setIncludeCanceled] = useState(false);
  const [includeFuture, setIncludeFuture] = useState(false);
  const [page, setPage] = useState(1);
  // Row class methods
  const getRowClass = <T extends ISession>(session: T): string => {
    let className = '';
    if (session.isCanceled) {
      className = 'text-grey';
    }
    return className;
  };
  const sessionParams = React.useMemo(
    () => ({
      page: page - 1,
      limit: defaultLimit || SESSIONS_LIMIT,
      includeFuture,
      includeCanceled,
      ...params
    }),
    [page, defaultLimit, includeFuture, includeCanceled, params]
  );
  const { sessions, total, run, isLoading } = useGetSessions<T>(
    url,
    sessionParams,
    transformer
  );
  if (
    setIntakeData !== null &&
    setIntakeData !== undefined &&
    sessions?.length > 0
  ) {
    const intakeData = sessions.filter(
      (session: any) => session.isRecheck === false && session.isLab === false
    )[0];
    setIntakeData(intakeData);
  }
  let preDiabetesSessionsWithoutIntake;
  if (filterOutIntake) {
    preDiabetesSessionsWithoutIntake = sessions.filter(
      (session: any) => session.isRecheck === true
    );
  }
  const dependencies = getDependencies ? getDependencies(sessions) : undefined;
  // Invalidation
  const [isIVDOpen, setIsIVDOpen] = useState(false);
  const [IVDProps, setIVDProps] = useState(DEFAULT_IVD_PROPS);
  const openInvalidation = (
    appKey: AppKey,
    sessionModel: string,
    session: ISession
  ) => {
    setIVDProps({
      appKey,
      sessionModel,
      session
    });
    setIsIVDOpen(true);
    return;
  };
  const closeInvalidation = () => {
    setIVDProps(DEFAULT_IVD_PROPS);
    setIsIVDOpen(false);
  };
  const onInvalidated = () => {
    run();
    closeInvalidation();
  };
  if (isLoading && sessions.length === 0) {
    return <LoadingBlock />;
  }
  const isSurvey: boolean = url.includes('/survey/');
  if (!sessions || sessions.length === 0) {
    return (
      <Card>
        <h3 className="mt-0">No responses</h3>
        {isSurvey ? (
          <>
            <p>
              There is currently no survey data for this program. Possible
              reasons for this to occur:
            </p>
            <ul>
              <li>The survey has not started yet</li>
              <li>
                The {terms.patient.default}'s prescription is not eligible for
                the quality survey
              </li>
            </ul>
          </>
        ) : (
          <>
            <p>
              There is currently no session data for this program. Possible
              reasons for this to occur:
            </p>
            <ul>
              <li>The program has not started yet</li>
              <li>
                The {terms.patient.default}'s prescription does not include
                these types of questions
              </li>
            </ul>
          </>
        )}
        <p className="mb-0">
          If you believe this is an error, please submit a support request.
        </p>
      </Card>
    );
  }
  // Compose columns
  let _columns = props.columns;
  if (getColumns != null) {
    _columns = getColumns(sessions);
  }
  const columns = ComposeColumns(props, _columns);
  return (
    <SessionTableProvider
      value={{
        openInvalidation,
        closeInvalidation
      }}
    >
      <div style={{ width: '100%' }}>
        {title && <h1>{title}</h1>}
        <Card
          className="pa-0 text-small"
          style={{
            overflowX: 'auto'
          }}
        >
          {type === 'vertical' ? (
            <VerticalTable<T>
              data={sessions}
              columns={columns}
              getRowClass={getRowClass}
              dependencies={dependencies}
              striped={striped == null ? true : striped}
              bareCell={bareCell}
            />
          ) : (
            <Table<T>
              data={
                filterOutIntake ? preDiabetesSessionsWithoutIntake : sessions
              }
              columns={columns}
              getRowClass={getRowClass}
              dependencies={dependencies}
              bordered
              striped={striped == null ? true : striped}
            />
          )}
        </Card>
        <div className="float-clear pt-8">
          {!disableControls && (
            <div className="float-left">
              <Switch
                disabled={disableFuture}
                className="inline-block mr-4 mt-4 ml-1"
                name="includeFuture"
                label="Include Future"
                checked={includeFuture}
                onChange={(event) =>
                  setIncludeFuture(event.currentTarget.checked)
                }
              />
              <Switch
                className="inline-block mt-4"
                name="includeCanceled"
                label="Include Canceled"
                checked={includeCanceled}
                onChange={(event) =>
                  setIncludeCanceled(event.currentTarget.checked)
                }
              />
            </div>
          )}
          <div className="float-right">
            <Pagination
              total={total}
              limit={defaultLimit || SESSIONS_LIMIT}
              onPageChange={(page) => setPage(page)}
            />
          </div>
        </div>
        <DebouncedDialog isOpen={isIVDOpen} onClose={closeInvalidation} lazy>
          <InvalidationForm
            {...IVDProps}
            onCancel={closeInvalidation}
            onSuccess={onInvalidated}
          />
        </DebouncedDialog>
      </div>
    </SessionTableProvider>
  );
};

export default SessionTable;
